import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Magazine } from 'app/models/magazine';
import { Observable, Subject, throwError } from 'rxjs';
import { tap, catchError, switchMap } from 'rxjs/operators';
import { AwscontrolService } from '../aws/awscontrol.service';

export interface IncrementResponse {
  success: boolean;
  message: string;
  magazine: {
    id: number;
    name: string;
    oldCount: number;
    increment: number;
    newCount: number;
    newDisplay: string;
    createdAt: string | null;
  };
  timestamp: string;
}

export interface MagazineStats {
  totalMagazines: number;
  totalDownloads: number;
  formattedTotalDownloads: string;
  activeMagazine: any;
  latestMagazine?: string;
  latestMagazineDownloads?: string;
  latestMagazineNumericDownloads?: number;
}

export interface BoostResponse {
  status: string;
  message: string;
  magazineId: number;
  multiplier: number;
  durationHours: number;
  expiresAt: string;
}

// NEW: Response for GET /magazines/boosts/active
export interface ActiveBoostsResponse {
  count: number;
  boosts: ActiveBoostItem[];
}

export interface ActiveBoostItem {
  magazineId: number;
  magazineName: string;
  imageUrl: string | null;
  downloadCount: number;
  boost: {
    multiplier: number;
    startedAt: string;
    expiresAt: string;
    remainingMinutes?: number;
  };
}

// NEW: Response for GET /magazines/{id}/boost-status
export interface BoostStatusResponse {
  hasBoost: boolean;
  message?: string;
  boost?: {
    multiplier: number;
    startedAt: string;
    expiresAt: string;
    remainingMinutes?: number;
  };
  magazineName?: string;
}

@Injectable({
  providedIn: 'root'
})
export class MagazineService {

  base_path = 'https://app.titv-store-api.com/';

  constructor(
    private http: HttpClient,
    private awsService: AwscontrolService
  ) { }

  private refreshData = new Subject<void>();
  
  get refreshNedeed() {
    return this.refreshData;
  }

  private getToken(): string | null {
    try {
      const authUser = sessionStorage.getItem('auth-user');
      if (authUser) {
        const userObj = JSON.parse(authUser);
        if (userObj && userObj.token) {
          return userObj.token;
        }
      }

      const directToken = sessionStorage.getItem('auth-token') || localStorage.getItem('auth-token');
      if (directToken) {
        return directToken;
      }

      return null;
    } catch (error) {
      console.error('❌ Erreur lors de la récupération du token:', error);
      return null;
    }
  }

  private getHeaders(): HttpHeaders {
    const token = this.getToken();
    
    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept': 'application/json, text/plain, */*'
    });
    
    if (token) {
      headers = headers.set('Authorization', 'Bearer ' + token);
    }
    
    return headers;
  }

  private handleError(error: HttpErrorResponse) {
    console.error('❌ MagazineService Error:', error);
    let errorMessage = 'Une erreur est survenue';
    if (error.status === 403) {
      errorMessage = 'Accès interdit (403). Vérifiez vos permissions.';
    } else if (error.status === 401) {
      errorMessage = 'Non autorisé. Veuillez vous reconnecter.';
    } else if (error.status === 404) {
      errorMessage = 'Ressource non trouvée (404).';
    } else if (error.status === 500) {
      errorMessage = 'Erreur serveur (500).';
    } else {
      errorMessage = `Erreur ${error.status}: ${error.message}`;
    }
    return throwError(() => new Error(errorMessage));
  }

  getListMagazine(): Observable<Magazine[]> {
    return this.http.get<Magazine[]>(this.base_path + 'magazines/all').pipe(
      catchError(this.handleError)
    );
  }

  getMagazine(id: any): Observable<Magazine> {
    return this.http.get<Magazine>(this.base_path + 'magazines/' + id).pipe(
      catchError(this.handleError)
    );
  }

  createMagazine(item: any): Observable<Magazine> {
    const headers = this.getHeaders();
    return this.http.post<Magazine>(this.base_path + 'magazines/add', item, { headers }).pipe(
      tap(() => this.refreshData.next()),
      catchError(this.handleError)
    );
  }

  updateMagazine(id: any, item: any): Observable<Magazine> {
    const headers = this.getHeaders();
    return this.http.put<Magazine>(this.base_path + 'magazines/edit/' + id, item, { headers }).pipe(
      tap(() => this.refreshData.next()),
      catchError(this.handleError)
    );
  }

  deleteMagazine(id: any): Observable<any> {
    const headers = this.getHeaders();
    return this.getMagazine(id).pipe(
      switchMap((magazine: any) => {
        if (magazine && magazine.imageUrl) {
          return this.awsService.deleteFile(magazine.imageUrl).pipe(
            catchError(() => [null]),
            switchMap(() => this.http.delete(this.base_path + 'magazines/delete/' + id, { headers, responseType: 'text' }))
          );
        } else {
          return this.http.delete(this.base_path + 'magazines/delete/' + id, { headers, responseType: 'text' });
        }
      }),
      tap(() => this.refreshData.next()),
      catchError(this.handleError)
    );
  }

  incrementDownloadCount(id: number): Observable<IncrementResponse> {
    const headers = this.getHeaders();
    return this.http.post<IncrementResponse>(
      this.base_path + 'magazines/admin/increment/' + id,
      {},
      { headers }
    ).pipe(
      tap((response) => {
        console.log('✅ Download count incremented:', response);
        this.refreshData.next();
      }),
      catchError((error: HttpErrorResponse) => {
        console.error('❌ Increment error details:', error);
        
        let errorMsg = 'Impossible d\'incrémenter le compteur. Erreur serveur.';
        
        if (error.error && typeof error.error === 'object') {
          if (error.error.message) {
            errorMsg = error.error.message;
          } else if (error.error.error) {
             errorMsg = error.error.error;
          }
        } else if (error.status === 500) {
           errorMsg = 'Erreur interne du serveur (500). Le magazine a peut-être des données manquantes (ex: nbr_telechargement null).';
        }

        return throwError(() => new Error(errorMsg));
      })
    );
  }

  // ==================== NEW METHODS (from Spring Boot controller) ====================

  getLatestMagazine(): Observable<Magazine> {
    return this.http.get<Magazine>(this.base_path + 'magazines/latest').pipe(
      catchError(this.handleError)
    );
  }

  getStats(): Observable<MagazineStats> {
    return this.http.get<MagazineStats>(this.base_path + 'magazines/stats').pipe(
      catchError(this.handleError)
    );
  }

  forceFocusOnLatest(): Observable<string> {
    const headers = this.getHeaders();
    return this.http.post(this.base_path + 'magazines/admin/focus-latest', {}, { headers, responseType: 'text' }).pipe(
      tap(() => this.refreshData.next()),
      catchError(this.handleError)
    );
  }

  activateBoost(id: number, multiplier: number, hours: number): Observable<BoostResponse> {
    const headers = this.getHeaders();
    const params = { multiplier: multiplier.toString(), hours: hours.toString() };
    return this.http.post<BoostResponse>(
      this.base_path + `magazines/boost/${id}`,
      null,
      { headers, params }
    ).pipe(
      tap((response) => console.log('✅ Boost activated:', response)),
      catchError(this.handleError)
    );
  }

  deactivateBoost(id: number): Observable<any> {
    const headers = this.getHeaders();
    return this.http.delete(this.base_path + `magazines/boost/${id}`, { headers }).pipe(
      tap((response) => console.log('✅ Boost deactivated:', response)),
      catchError(this.handleError)
    );
  }

  // ==================== MISSING BOOST FUNCTIONS (ADDED) ====================

  /**
   * GET /magazines/boosts/active
   * Returns all magazines that currently have an active sponsored boost.
   */
  getActiveBoosts(): Observable<ActiveBoostsResponse> {
    return this.http.get<ActiveBoostsResponse>(this.base_path + 'magazines/boosts/active').pipe(
      catchError(this.handleError)
    );
  }

  /**
   * GET /magazines/{id}/boost-status
   * Returns boost details for a specific magazine if active.
   */
  getBoostStatusForMagazine(id: number): Observable<BoostStatusResponse> {
    return this.http.get<BoostStatusResponse>(this.base_path + `magazines/${id}/boost-status`).pipe(
      catchError(this.handleError)
    );
  }

  testToken(): boolean {
    const token = this.getToken();
    return !!token;
  }
}